import * as THREE from "three";

import { SceneManager } from "../../../core/scene_manager.js";
import { ResourceManager } from "../../../resource_loader/resource_manager.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";

export class ConveyorArt
{
  private static readonly GEAR_DIAMETER_CUBES = 1.1;
  private static readonly GEAR_SPACING_DIAMETERS = 2;
  private static readonly FALLBACK_COLOR = 0x808080;
  private static readonly BOTTOM_RUN_SHADE = 0.4;

  private readonly root = new THREE.Group();
  private readonly top_run = new THREE.Group();
  private readonly bottom_run = new THREE.Group();
  private readonly gears: THREE.Object3D[] = [];
  private readonly materials: THREE.Material[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];

  private readonly run_left_x: number;
  private readonly strip_length: number;
  private readonly gear_radius: number;
  private readonly direction: number;
  private travel = 0;

  constructor(
    left_x: number,
    right_x: number,
    surface_y: number,
    center_z: number,
    depth: number,
    cube_size: THREE.Vector3,
    direction: number = 1
  )
  {
    this.direction = direction;

    const strip = this.makeStrip( depth, cube_size );
    const strip_size = new THREE.Box3().setFromObject( strip ).getSize( new THREE.Vector3() );
    const gear_diameter = cube_size.y * ConveyorArt.GEAR_DIAMETER_CUBES;

    this.run_left_x = left_x;
    this.strip_length = strip_size.x;
    this.gear_radius = gear_diameter * 0.5;

    const gear_y = surface_y - strip_size.y - this.gear_radius;
    const bottom_run_y = gear_y - this.gear_radius;

    this.buildRuns( strip, right_x - left_x );
    this.top_run.position.set( left_x, surface_y, center_z );
    this.bottom_run.position.set( left_x, bottom_run_y, center_z );

    this.buildGears( left_x, right_x, gear_y, center_z + depth * 0.5, gear_diameter );

    this.root.add( this.top_run, this.bottom_run );
    SceneManager.world.add( this.root );
  }

  public update( dt: number, speed: number ): void
  {
    if ( speed === 0 )
      return;

    this.travel = ( this.travel + speed * dt ) % this.strip_length;
    this.top_run.position.x = this.run_left_x + this.travel * this.direction;
    this.bottom_run.position.x = this.run_left_x - this.travel * this.direction;

    const turn = speed * dt / this.gear_radius;

    for ( const gear of this.gears )
      gear.rotation.z -= turn * this.direction;
  }

  public dispose(): void
  {
    SceneManager.world.remove( this.root );

    for ( const material of this.materials )
      material.dispose();

    this.materials.length = 0;

    for ( const geometry of this.geometries )
      geometry.dispose();

    this.geometries.length = 0;
  }

  private buildRuns( strip: THREE.Object3D, length: number ): void
  {
    const count = Math.ceil( length / this.strip_length ) + 2;
    const bottom_strip = this.makeShadedCopy( strip, ConveyorArt.BOTTOM_RUN_SHADE );

    for ( let i = 0; i < count; i++ )
    {
      const x = ( i - 1 ) * this.strip_length;
      const top = strip.clone();
      const bottom = bottom_strip.clone();

      top.position.x = x;
      bottom.position.x = x;
      this.top_run.add( top );
      this.bottom_run.add( bottom );
    }
  }

  private buildGears( left_x: number, right_x: number, y: number, front_z: number, diameter: number ): void
  {
    const template = this.makeGear( diameter );
    const half_thickness = new THREE.Box3().setFromObject( template ).getSize( new THREE.Vector3() ).z * 0.5;
    const length = right_x - left_x;
    const count = Math.max( 2, Math.round( length / ( diameter * ConveyorArt.GEAR_SPACING_DIAMETERS ) ) );
    const z = front_z - half_thickness;

    for ( let i = 0; i < count; i++ )
    {
      const gear = template.clone();
      const x = left_x + length * ( i + 0.5 ) / count;

      gear.position.set( x, y, z );
      this.gears.push( gear );
      this.root.add( gear );
    }
  }

  private makeStrip( depth: number, cube_size: THREE.Vector3 ): THREE.Group
  {
    const model = ResourceManager.getModel( ResourceID.ConveyorModel );
    this.applyGameMaterials( model, false );

    const size = new THREE.Box3().setFromObject( model ).getSize( new THREE.Vector3() );
    const along = cube_size.z / size.z;

    model.scale.multiply( new THREE.Vector3( along, along, depth / size.z ) );
    model.updateMatrixWorld( true );

    const box = new THREE.Box3().setFromObject( model );
    model.position.set( -box.min.x, -box.max.y, -( box.min.z + box.max.z ) * 0.5 );

    return new THREE.Group().add( model );
  }

  private makeShadedCopy( source: THREE.Object3D, shade: number ): THREE.Object3D
  {
    const copy = source.clone();
    const shaded = new Map<THREE.Material, THREE.Material>();

    copy.traverse( obj =>
    {
      if ( !( obj instanceof THREE.Mesh ) )
        return;

      const originals: THREE.Material[] = Array.isArray( obj.material ) ? obj.material : [ obj.material ];
      const darker = originals.map( m =>
      {
        if ( !shaded.has( m ) )
        {
          const material = ( m as THREE.MeshLambertMaterial ).clone();
          material.color.multiplyScalar( shade );
          this.materials.push( material );
          shaded.set( m, material );
        }

        return shaded.get( m )!;
      } );

      obj.material = darker.length === 1 ? darker[0] : darker;
    } );

    return copy;
  }

  private makeGear( diameter: number ): THREE.Group
  {
    const model = ResourceManager.getModel( ResourceID.GearModel );
    this.applyGameMaterials( model, true );

    const size = new THREE.Box3().setFromObject( model ).getSize( new THREE.Vector3() );
    model.scale.multiplyScalar( diameter / Math.max( size.x, size.y ) );
    model.updateMatrixWorld( true );

    const center = new THREE.Box3().setFromObject( model ).getCenter( new THREE.Vector3() );
    model.position.sub( center );

    return new THREE.Group().add( model );
  }

  private applyGameMaterials( model: THREE.Object3D, cast_shadow: boolean ): void
  {
    let painted: THREE.MeshLambertMaterial | null = null;

    model.traverse( obj =>
    {
      if ( !( obj instanceof THREE.Mesh ) )
        return;

      const authored: THREE.Material[] = Array.isArray( obj.material ) ? obj.material : [ obj.material ];
      const geometry = authored.length > 1
        ? this.paintGroups( obj.geometry, authored.map( m => ConveyorArt.authoredColor( m ) ) )
        : null;

      if ( geometry )
      {
        painted ??= this.paintedMaterial();
        obj.geometry = geometry;
        obj.material = painted;
      }
      else
      {
        const game = authored.map( m => this.gameMaterial( m ) );
        obj.material = game.length === 1 ? game[0] : game;
      }

      obj.castShadow = cast_shadow;
      obj.receiveShadow = true;
    } );
  }

  private paintGroups( source: THREE.BufferGeometry, colors: THREE.Color[] ): THREE.BufferGeometry | null
  {
    if ( source.groups.length === 0 )
      return null;

    const geometry = source.index ? source.toNonIndexed() : source.clone();
    const count = geometry.attributes.position.count;
    const covered = new Uint8Array( count );
    const painted = new Float32Array( count * 3 );

    for ( const group of geometry.groups )
    {
      const color = colors[group.materialIndex ?? 0];
      if ( !color )
      {
        geometry.dispose();
        return null;
      }

      const end = Math.min( count, group.start + group.count );
      for ( let i = group.start; i < end; i++ )
      {
        covered[i] = 1;
        painted[i * 3] = color.r;
        painted[i * 3 + 1] = color.g;
        painted[i * 3 + 2] = color.b;
      }
    }

    if ( covered.includes( 0 ) )
    {
      geometry.dispose();
      return null;
    }

    geometry.clearGroups();
    geometry.setAttribute( "color", new THREE.BufferAttribute( painted, 3 ) );
    this.geometries.push( geometry );

    return geometry;
  }

  private paintedMaterial(): THREE.MeshLambertMaterial
  {
    const material = new THREE.MeshLambertMaterial( { vertexColors: true } );

    this.materials.push( material );

    return material;
  }

  private static authoredColor( authored: THREE.Material ): THREE.Color
  {
    return ( authored as THREE.MeshPhongMaterial ).color?.clone() ?? new THREE.Color( ConveyorArt.FALLBACK_COLOR );
  }

  private gameMaterial( authored: THREE.Material ): THREE.Material
  {
    const material = new THREE.MeshLambertMaterial( { color: ConveyorArt.authoredColor( authored ) } );

    this.materials.push( material );

    return material;
  }
}
